/**
 * Shared report table layout + localStorage persistence (Order 31H).
 */
import { useCallback, useEffect, useMemo, useState } from "react";

export type ReportTableLayoutConfig<T extends string> = {
  readonly storageKey: string;
  readonly defaultOrder: readonly T[];
  readonly requiredColumns: ReadonlySet<T>;
  readonly defaultWidths: Readonly<Record<T, number>>;
  readonly minWidths: Readonly<Record<T, number>>;
  readonly maxWidths: Readonly<Record<T, number>>;
  readonly defaultHidden?: readonly T[];
};

type StoredLayout<T extends string> = {
  order: T[];
  hidden: T[];
  widths: Partial<Record<T, number>>;
};

function clampWidth<T extends string>(
  config: ReportTableLayoutConfig<T>,
  id: T,
  width: number,
): number {
  const min = config.minWidths[id] ?? 72;
  const max = config.maxWidths[id] ?? 480;
  return Math.min(max, Math.max(min, Math.round(width)));
}

function normalizeOrder<T extends string>(
  config: ReportTableLayoutConfig<T>,
  order: readonly T[],
): T[] {
  const known = new Set(config.defaultOrder);
  const next = order.filter((id) => known.has(id));
  for (const id of config.defaultOrder) {
    if (!next.includes(id)) next.push(id);
  }
  return next;
}

function loadStored<T extends string>(
  config: ReportTableLayoutConfig<T>,
): StoredLayout<T> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(config.storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredLayout<T>;
    if (!Array.isArray(parsed.order) || !Array.isArray(parsed.hidden)) return null;
    return {
      order: normalizeOrder(config, parsed.order),
      hidden: parsed.hidden.filter((id): id is T => config.defaultOrder.includes(id)),
      widths:
        parsed.widths && typeof parsed.widths === "object" ? parsed.widths : {},
    };
  } catch {
    return null;
  }
}

function saveStored<T extends string>(
  config: ReportTableLayoutConfig<T>,
  prefs: StoredLayout<T>,
): void {
  try {
    window.localStorage.setItem(config.storageKey, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function useReportTableLayout<T extends string>(
  config: ReportTableLayoutConfig<T>,
): {
  columnOrder: T[];
  visibleColumns: T[];
  hiddenColumns: Set<T>;
  widths: Record<T, number>;
  toggleColumn: (id: T) => void;
  moveColumn: (id: T, direction: "up" | "down") => void;
  reorderColumn: (fromId: T, toId: T) => void;
  resizeColumn: (id: T, width: number) => void;
  resetColumns: () => void;
} {
  const stored = loadStored(config);
  const [columnOrder, setColumnOrder] = useState<T[]>(
    () => stored?.order ?? [...config.defaultOrder],
  );
  const [hiddenColumns, setHiddenColumns] = useState<Set<T>>(
    () => new Set(stored?.hidden ?? config.defaultHidden ?? []),
  );
  const [widths, setWidths] = useState<Record<T, number>>(() => {
    const base: Record<T, number> = { ...config.defaultWidths };
    if (stored?.widths) {
      for (const id of config.defaultOrder) {
        const w = stored.widths[id];
        if (typeof w === "number") base[id] = clampWidth(config, id, w);
      }
    }
    return base;
  });

  const visibleColumns = useMemo(
    () => columnOrder.filter((id) => !hiddenColumns.has(id)),
    [columnOrder, hiddenColumns],
  );

  useEffect(() => {
    saveStored(config, {
      order: columnOrder,
      hidden: [...hiddenColumns],
      widths,
    });
  }, [columnOrder, config, hiddenColumns, widths]);

  const toggleColumn = useCallback(
    (id: T) => {
      if (config.requiredColumns.has(id)) return;
      setHiddenColumns((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        const remaining = columnOrder.filter((colId) => colId !== id && !next.has(colId)).length;
        if (remaining === 0) return prev;
        next.add(id);
        return next;
      });
    },
    [columnOrder, config.requiredColumns],
  );

  const moveColumn = useCallback((id: T, direction: "up" | "down") => {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id);
      if (index < 0) return prev;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item!);
      return next;
    });
  }, []);

  const reorderColumn = useCallback((fromId: T, toId: T) => {
    setColumnOrder((prev) => {
      const from = prev.indexOf(fromId);
      const to = prev.indexOf(toId);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item!);
      return next;
    });
  }, []);

  const resizeColumn = useCallback(
    (id: T, width: number) => {
      setWidths((prev) => ({ ...prev, [id]: clampWidth(config, id, width) }));
    },
    [config],
  );

  const resetColumns = useCallback(() => {
    setColumnOrder([...config.defaultOrder]);
    setHiddenColumns(new Set(config.defaultHidden ?? []));
    setWidths({ ...config.defaultWidths });
  }, [config]);

  return {
    columnOrder,
    visibleColumns,
    hiddenColumns,
    widths,
    toggleColumn,
    moveColumn,
    reorderColumn,
    resizeColumn,
    resetColumns,
  };
}
